// shop-frontend/src/utils/messagesApi.js
// ──────────────────────────────────────────────────────────────
// Messaging-specific helpers for ShopMessages.js.
// Delegates all auth + fetch to the shared api.js layer so token
// handling, base URL resolution, and error semantics stay in one place.
// ──────────────────────────────────────────────────────────────

import { apiGet, apiPost, apiDelete, request } from "./api";

/* =========================
   ✅ Normalizers (IMPORTANT)
   Backend returns conversation objects in several shapes;
   these normalizers guarantee a stable { _id, id, conversationId } triple.
   ========================= */

function pickConversationId(c) {
  return (
    c?.conversationId ||
    c?._id ||
    c?.id ||
    c?.conversation?._id ||
    c?.conversation?.id ||
    ""
  );
}

function normalizeConversation(c) {
  const cid = pickConversationId(c);
  return {
    ...c,
    conversationId: cid || c?.conversationId || "",
    _id: cid || c?._id || "",
    id: cid || c?.id || "",
  };
}

function normalizeConversationListResponse(data) {
  // backend might return:
  // 1) [ ... ]
  // 2) { conversations: [ ... ] }
  // 3) { ok:true, conversations:[ ... ] }
  // 4) { data:[ ... ] }
  const list = Array.isArray(data)
    ? data
    : Array.isArray(data?.conversations)
    ? data.conversations
    : Array.isArray(data?.data)
    ? data.data
    : [];

  return list.map(normalizeConversation);
}

function ensureConversationId(conversationId) {
  const cid = String(conversationId || "").trim();
  if (!cid) {
    const err = new Error("Missing conversationId");
    err.code = "MISSING_CONVERSATION_ID";
    throw err;
  }
  return cid;
}

/**
 * Expected backend paths (api.js prepends /api automatically):
 * GET  /messages/conversations
 * GET  /messages/conversations/:id/messages
 * POST /messages/conversations/:id/messages
 * POST /messages/conversations   (start new convo)
 */

// ✅ List conversations (normalized)
export async function listConversations() {
  const data = await apiGet("/messages/conversations");
  return normalizeConversationListResponse(data);
}

// ✅ Load messages for a conversation
export async function getConversationMessages(conversationId) {
  const cid = ensureConversationId(conversationId);
  return apiGet(`/messages/conversations/${cid}/messages`);
}

/**
 * ✅ Send message (text + optional files)
 * Supports BOTH call styles:
 *   A) sendMessage(conversationId, { text, files })
 *   B) sendMessage({ conversationId, text, files })
 */
export async function sendMessage(arg1, arg2) {
  let conversationId = "";
  let payload = null;

  if (typeof arg1 === "object" && arg1 !== null && !Array.isArray(arg1)) {
    conversationId = arg1.conversationId;
    payload = arg1;
  } else {
    conversationId = arg1;
    payload = arg2 || {};
  }

  const cid = ensureConversationId(conversationId);

  // Callers pass a FormData (shop chat with files) or a plain string (Cart Leads);
  // both used to be read as {text:""} and the message was refused as empty.
  if (typeof FormData !== "undefined" && payload instanceof FormData) {
    return request(`/messages/conversations/${cid}/messages`, { method: "POST", body: payload });
  }
  if (typeof payload === "string") payload = { text: payload };

  const text = payload?.text || "";
  const files = payload?.files || [];

  // File uploads: use FormData (request() skips JSON serialization for FormData)
  if (files && files.length) {
    const fd = new FormData();
    fd.append("text", text || "");
    for (const f of files) fd.append("files", f);

    return request(`/messages/conversations/${cid}/messages`, { method: "POST", body: fd });
  }

  // Text-only message
  return apiPost(`/messages/conversations/${cid}/messages`, { text: text || "" });
}

// ✅ Start a new conversation with a user OR a shop
export async function startConversation({ participantType, participantId }) {
  return apiPost("/messages/conversations", { participantType, participantId });
}

// ✅ Delete a message
export async function deleteMessage(conversationId, messageId) {
  const cid = ensureConversationId(conversationId);
  const mid = String(messageId || "").trim();
  if (!mid) throw new Error("Missing messageId");

  return apiDelete(`/messages/conversations/${cid}/messages/${mid}`);
}

// ✅ Delete a conversation (soft delete — hides for the user)
export async function deleteConversation(conversationId) {
  const cid = ensureConversationId(conversationId);
  return apiDelete(`/messages/conversations/${cid}`);
}


// Unread messages across this shop's chats (for the sidebar badge).
export async function getUnreadMessageCount() {
  const data = await apiGet("/messages/unread-count");
  return Number(data?.count || 0);
}
